import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { format } from 'url';
import config from 'config';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import {
  handleBack,
  formatRelativeTime,
  parseSchedule,
  getStatusClass,
  formatScanSummary,
  authMapperSync,
  getStatsFromSummary,
  formatStatusText,
  formatRunStatusText,
  getScanErrorMessage
} from '../../utils/utils';
import {
  scannerPropType,
  scannerRunPropType,
  ScannerStatusEnum,
  POLLING_INTERVAL_MS,
  FORM_ASSET_TYPE_LABELS
} from '~/components/AgentScanners/types/commonTypes';
import ScanConfigurationActions from '../ScanConfigurationActions';
import { getPath } from '~/utils/routes';
import styles from './ScannerDetail.css';
import { RunStatusEnum } from '../../types/commonTypes';

const SCAN_FAILED_MESSAGE = "We couldn't complete the scan. Try again later.";

// Re-export for tests
export { POLLING_INTERVAL_MS };

// Statuses that trigger automatic polling for updates (using Set for O(1) lookup)
export const POLLING_STATUSES = new Set([
  ScannerStatusEnum.QUEUED.key.toUpperCase(),
  ScannerStatusEnum.RUNNING.key.toUpperCase(),
  ScannerStatusEnum.IMPORTING.key.toUpperCase()
]);

const ScannerDetail = ({
  scanner,
  scannerId,
  organizationId,
  isFetching,
  scannerRuns,
  isFetchingScannerRuns,
  scannerRunsError,
  loadScanners,
  loadScannerRuns,
  clearScannerRuns,
  onRunNowClick,
  onAbortClick,
  onDeleteClick,
  onEditClick,
  setAlert
}) => {
  const history = useHistory();
  const lightningIconURL = format({
    ...config.images.uri,
    pathname: 'lightning-small.svg'
  });

  const authDisplayValue = React.useMemo(() => {
    const authScheme = scanner?.connection?.authScheme;

    if (authScheme) {
      return authMapperSync(authScheme) || '-';
    }

    return '-';
  }, [scanner?.connection?.authScheme]);

  const assetTypeLabel = React.useMemo(() => {
    const assetType = scanner?.connection?.targetSystem?.assetType;

    return FORM_ASSET_TYPE_LABELS[assetType?.toLowerCase()] || 'Agents';
  }, [scanner?.connection?.targetSystem?.assetType]);

  // Track the last scannerId we attempted to load to prevent infinite retries
  const lastLoadedScannerId = React.useRef(null);

  // Load scanner runs when component mounts or scanner changes
  React.useEffect(() => {
    // Only load if we have the required IDs and haven't already loaded for this scanner
    // Don't retry if there's already an error for this scanner
    if (
      organizationId &&
      scannerId &&
      lastLoadedScannerId.current !== scannerId &&
      !scannerRunsError
    ) {
      lastLoadedScannerId.current = scannerId;
      loadScannerRuns(organizationId, scannerId);
    }

    // Clear runs when unmounting
    return () => {
      clearScannerRuns();
    };
  }, [organizationId, scannerId]);

  // Fire the "Import is completed" toast once polling observes the scanner
  // transition out of IMPORTING into a successful run state. We track the
  // previous scanner state in a ref so we only toast on the transition edge.
  const prevScannerStateRef = React.useRef(scanner?.state?.toUpperCase());

  React.useEffect(() => {
    const prevState = prevScannerStateRef.current;
    const currentState = scanner?.state?.toUpperCase();

    const wasImporting = prevState === 'IMPORTING';
    const latestRunStatus = scannerRuns[0]?.status?.toUpperCase();
    const importCompleted =
      wasImporting &&
      currentState !== 'IMPORTING' &&
      (latestRunStatus === 'COMPLETED' ||
        latestRunStatus === 'PARTIALSUCCESS' ||
        latestRunStatus === 'PARTIAL_SUCCESS');

    if (importCompleted && setAlert) {
      setAlert({
        type: 'success',
        message: 'Import is completed.',
        isHTML: false
      });
    }

    prevScannerStateRef.current = currentState;
  }, [scanner?.state, scannerRuns, setAlert]);

  // Auto-polling for scanner updates when status is QUEUED, RUNNING, or IMPORTING
  React.useEffect(() => {
    // Check if the scanner state requires polling
    const scannerState = scanner?.state?.toUpperCase();
    const shouldPoll = scannerState && POLLING_STATUSES.has(scannerState);

    // Verify all required dependencies exist before polling
    if (
      !shouldPoll ||
      !organizationId ||
      !scannerId ||
      !loadScanners ||
      !loadScannerRuns
    ) {
      return undefined;
    }

    // Set up polling interval to refresh scanner and runs data
    const pollIntervalId = setInterval(() => {
      // Reload scanners list to get updated scanner information
      loadScanners(organizationId);
      // Reload scanner runs to get updated scan history
      loadScannerRuns(organizationId, scannerId);
    }, POLLING_INTERVAL_MS);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(pollIntervalId);
    };
  }, [
    scanner?.state,
    organizationId,
    scannerId,
    loadScanners,
    loadScannerRuns
  ]);

  const lastRunStatusDetailLower = (
    scanner?.lastRunStatusDetail ?? ''
  ).toLowerCase();

  // Memoized callback for back navigation
  const handleBackClick = React.useCallback(() => {
    handleBack(history);
  }, [history]);

  // Navigates to the scan audit log page for a specific run
  const handleRunScanClick = React.useCallback(
    (runScanId, runStatus) => {
      const upperStatus = (runStatus || '').toUpperCase();

      if (upperStatus === 'IMPORTING' || upperStatus === 'RUNNING') {
        setAlert({
          type: 'info',
          message:
            'Scan in progress. The review screen will become available once the scan has completed.',
          isHTML: false
        });

        return;
      }

      history.push(`scanAuditLog/${runScanId}`);
    },
    [history, setAlert]
  );

  // Navigates to the scan audit log for the most recent run
  const handleReviewAndImport = React.useCallback(() => {
    const latestRunId = scannerRuns[0]?.id;

    if (latestRunId) {
      history.push(getPath('scanAuditLog', { scannerId, logId: latestRunId }));
    }
  }, [history, scannerRuns, scannerId]);

  // Handle retry loading scanner runs
  const handleRetryLoadRuns = React.useCallback(() => {
    if (organizationId && scannerId) {
      // Reset the ref to allow retry
      lastLoadedScannerId.current = null;
      loadScannerRuns(organizationId, scannerId);
    }
  }, [organizationId, scannerId, loadScannerRuns]);

  // Handle loading state - only show full page loading on initial load (when no scanner data exists)
  // During polling refreshes, we keep showing the existing data
  if (isFetching && !scanner) {
    return (
      <div className={styles.page} data-test-id="scanner-detail-loading">
        <p>Loading scanner details...</p>
      </div>
    );
  }

  // Handle scanner not found (only when not fetching)
  if (!isFetching && !scanner) {
    return (
      <div className={styles.page} data-test-id="scanner-detail-not-found">
        <h1>Scanner not found</h1>
        <p>The requested scanner could not be found.</p>
        <Button
          testId="not-found-back-button"
          kind="secondary"
          onClick={handleBackClick}
        >
          Back to List
        </Button>
      </div>
    );
  }

  const isPendingReview = scannerRuns[0]?.status === 'PENDING_REVIEW';
  const lastScanStats = isPendingReview
    ? getStatsFromSummary(scannerRuns[0]?.summary)
    : getStatsFromSummary(scanner.lastSuccessfulRunSummary);
  const totalActions = isPendingReview
    ? (lastScanStats?.added ?? 0) +
      (lastScanStats?.updated ?? 0) +
      (lastScanStats?.removed ?? 0)
    : 0;

  return (
    <div
      className={styles.page}
      id="scannerDetailPage"
      data-test-id="scanner-detail-page"
    >
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.breadcrumbs}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackClick}
            data-test-id="back-to-list"
          >
            Scanners
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span
            className={styles.breadcrumbCurrent}
            data-test-id="scanner-name-breadcrumb"
          >
            {scanner.name}
          </span>
        </div>

        <div className={styles.detailHeader}>
          <div className={styles.detailTitleSection}>
            <h1
              className={styles.detailTitle}
              data-test-id="scanner-name-heading"
            >
              {scanner.name}
            </h1>
          </div>
          <ScanConfigurationActions
            variant="button"
            onRun={() => onRunNowClick(scanner, organizationId)}
            onAbort={() => onAbortClick(scanner, organizationId)}
            onEdit={() => onEditClick(scannerId)}
            onDelete={() => onDeleteClick(scanner)}
            scannerStatus={scanner.state}
          />
        </div>
      </div>

      {/* Credentials failure pink banner */}
      {scanner.state.toLowerCase() === ScannerStatusEnum.STOPPED.key &&
        scanner.lastRunStatus.toLowerCase() === RunStatusEnum.FAILED.key &&
        lastRunStatusDetailLower.length > 0 &&
        (lastRunStatusDetailLower.includes('authentication failed') ||
          lastRunStatusDetailLower.includes('permission denied') ||
          lastRunStatusDetailLower.includes('access denied')) && (
          <div
            className={styles.credentialsBanner}
            role="alert"
            data-test-id="credentials-update-banner"
          >
            <Icon
              name="error-filled"
              size={24}
              className={styles.credentialsBannerIcon}
              aria-hidden
            />
            <div className={styles.credentialsBannerContent}>
              <div className={styles.credentialsBannerTitle}>
                We couldn&apost run the scheduled scan.
              </div>
              <div className={styles.credentialsBannerDescription}>
                Update your credentials to reactivate the schedule.
              </div>
              <button
                type="button"
                className={styles.credentialsBannerLink}
                onClick={() => onEditClick(scannerId)}
                data-test-id="credentials-update-link"
                aria-label="Update credentials to reactivate scheduled scan"
              >
                Update credentials
              </button>
            </div>
          </div>
        )}

      {/* Scanner Details Content */}
      <div className={styles.detailContent}>
        {/* Scanner information */}
        <div
          className={styles.detailSection}
          data-test-id="scanner-information-section"
        >
          <h3 className={styles.sectionTitle}>Scanner Information</h3>
          <div className={styles.detailSectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Scanner Name</span>
                <span data-test-id="scanner-name">{scanner.name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status</span>
                <span
                  className={`${styles.statusBadge} ${getStatusClass(scanner.state, styles)}`}
                  data-test-id="scanner-status"
                >
                  {formatStatusText(scanner.state)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Next Run</span>
                <span data-test-id="scanner-next-run">
                  {scanner.nextRunAt
                    ? formatRelativeTime(scanner.nextRunAt)
                    : '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Run Schedule</span>
                <span data-test-id="scanner-schedule">
                  {parseSchedule(scanner.schedule) || '-'}
                  <Tooltip
                    content="This time is in your local timezone"
                    position="top"
                    testId="run-schedule-tooltip"
                    theme={styles}
                  >
                    <span>
                      <Icon
                        name="info-small"
                        size={18}
                        className={styles.infoIcon}
                      />
                    </span>
                  </Tooltip>
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Completed Run</span>
                <span data-test-id="scanner-last-completed-run">
                  {formatRelativeTime(scanner.lastSuccessfulRunAt)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Run Status</span>
                <span
                  className={
                    scanner.lastRunStatus
                      ? `${getStatusClass(scanner.lastRunStatus, styles)}`
                      : ''
                  }
                  data-test-id="scanner-last-run-status"
                >
                  {formatRunStatusText(scanner.lastRunStatus)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Created</span>
                <span data-test-id="scanner-created-at">
                  {formatRelativeTime(scanner.createdAt)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Description</span>
                <Tooltip
                  content={scanner.description}
                  position="bottom"
                  testId="description-tooltip"
                  theme={styles}
                >
                  <div className={styles.infoLabelTooLongContainer}>
                    <span
                      className={styles.infoLabelTooLongText}
                      data-test-id="scanner-instance-name"
                    >
                      {scanner.description}
                    </span>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* Connection information */}
        <div
          className={styles.detailSection}
          data-test-id="connection-information-section"
        >
          <h3 className={styles.sectionTitle}>Connection Details</h3>
          <div className={styles.detailSectionContent}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Agent Provider</span>
                <span data-test-id="target-system">
                  {scanner.connection?.targetSystem?.name || '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Authentication</span>
                <span data-test-id="auth-method">{authDisplayValue}</span>
                <input
                  type="hidden"
                  value={scanner.connection?.authScheme || ''}
                  data-test-id="auth-scheme-hidden"
                />
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Last Connection Test</span>
                <span data-test-id="last-connection-test">
                  {formatRelativeTime(scanner.connection?.lastTestedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Last Scan Results */}
        <div
          className={styles.detailSection}
          data-test-id="last-scan-results-section"
        >
          <h3 className={styles.sectionTitle}>
            {isPendingReview
              ? 'Review Last Scan Results'
              : 'Last Completed Scan'}{' '}
            -{' '}
            {formatRelativeTime(
              isPendingReview
                ? scannerRuns[0]?.startedAt
                : scanner.lastSuccessfulRunAt
            )}
          </h3>
          <div className={styles.detailSectionContentNoBorder}>
            {(() => {
              return (
                <div className={styles.syncResultsSummary}>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-added"
                  >
                    <div className={styles.statLabel}>
                      {isPendingReview
                        ? `${assetTypeLabel} to Add`
                        : `${assetTypeLabel} Added`}
                    </div>
                    <div className={styles.statNumber}>
                      {lastScanStats?.added ?? 0}
                    </div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-updated"
                  >
                    <div className={styles.statLabel}>
                      {isPendingReview
                        ? `${assetTypeLabel} to Update`
                        : `${assetTypeLabel} Updated`}
                    </div>
                    <div className={styles.statNumber}>
                      {lastScanStats?.updated ?? 0}
                    </div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-removed"
                  >
                    <div className={styles.statLabel}>
                      {isPendingReview
                        ? `${assetTypeLabel} Missing From Exchange`
                        : `${assetTypeLabel} Removed`}
                    </div>
                    <div className={styles.statNumber}>
                      {lastScanStats?.removed ?? 0}
                    </div>
                  </div>
                  <div
                    className={styles.syncResultStat}
                    data-test-id="stat-unchanged"
                  >
                    <div className={styles.statLabel}>
                      {isPendingReview
                        ? `${assetTypeLabel} Already in Exchange`
                        : `${assetTypeLabel} in Exchange`}
                    </div>
                    <div className={styles.statNumber}>
                      {lastScanStats?.unchanged ?? 0}
                    </div>
                  </div>
                  {isPendingReview && (
                    <div
                      className={styles.syncResultStat}
                      data-test-id="stat-skipped"
                    >
                      <div className={styles.statLabel}>
                        {`${assetTypeLabel} Skipped in Last Review`}
                      </div>
                      <div className={styles.statNumber}>
                        {lastScanStats?.ignored ?? 0}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Review & Import Action Bar */}
        {isPendingReview && (
          <div
            className={styles.reviewActionBar}
            data-test-id="review-action-bar"
          >
            <div className={styles.reviewActionInfo}>
              <img
                src={lightningIconURL}
                className={styles.reviewActionIcon}
                aria-hidden="true"
                alt=""
              />
              <span
                className={styles.reviewActionLabel}
                data-test-id="review-action-count"
              >
                {totalActions} Actions available
              </span>
            </div>
            <Button
              kind="primary"
              testId="review-and-import-button"
              onClick={handleReviewAndImport}
              disabled={
                !scannerRuns[0]?.id ||
                scannerRuns[0]?.status !== 'PENDING_REVIEW'
              }
            >
              Review &amp; Import
            </Button>
          </div>
        )}

        {/* Scan history */}
        <h3 className={styles.sectionTitle}>Scan History</h3>
        <div
          className={styles.detailSection}
          data-test-id="scan-history-section"
        >
          <div className={styles.detailSectionContent}>
            {/* Show loading only on initial load (when no runs data exists) */}
            {isFetchingScannerRuns && scannerRuns.length === 0 && (
              <div className={styles.loadingRuns} data-test-id="runs-loading">
                Loading scan history...
              </div>
            )}
            {/* Show error only when not fetching */}
            {!isFetchingScannerRuns && scannerRunsError && (
              <div className={styles.errorRuns} data-test-id="runs-error">
                <div style={{ marginBottom: '10px' }}>
                  Error loading scan history: {scannerRunsError.message}
                </div>
                <Button
                  testId="retry-load-runs"
                  kind="secondary"
                  onClick={handleRetryLoadRuns}
                >
                  Retry
                </Button>
              </div>
            )}
            {/* Show empty state only when not fetching and no runs */}
            {!isFetchingScannerRuns &&
              !scannerRunsError &&
              scannerRuns.length === 0 && (
                <div className={styles.emptyRuns} data-test-id="runs-empty">
                  No scan history available.
                </div>
              )}
            {/* Show runs list if we have data (even during background refresh) */}
            {!scannerRunsError && scannerRuns.length > 0 && (
              <div className={styles.scanHistoryList}>
                {scannerRuns.map((run, index) => (
                  <div
                    role="button"
                    aria-label="Scan audit log"
                    tabIndex="0"
                    key={run.createdAt || index}
                    className={styles.historyItem}
                    data-test-id={`history-item-${index}`}
                    onClick={() => handleRunScanClick(run.id, run.status)}
                    onKeyDown={() => handleRunScanClick(run.id, run.status)}
                  >
                    <div className={styles.historyTime}>
                      {formatRelativeTime(run.startedAt)}
                    </div>
                    <div
                      className={`${styles.historyStatus} ${getStatusClass(run.status, styles)}`}
                      data-test-id={`history-status-${index}`}
                    >
                      {formatRunStatusText(run.status)}
                    </div>
                    {run.status !== 'IMPORTING' && run.status !== 'RUNNING' && (
                      <div
                        className={
                          run.status === 'FAILED'
                            ? styles.historyErrorMessage
                            : styles.historySummary
                        }
                        data-test-id={`history-summary-${index}`}
                      >
                        {run.status === 'FAILED'
                          ? getScanErrorMessage(run.summary) ||
                            SCAN_FAILED_MESSAGE
                          : formatScanSummary(run.summary)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ScannerDetail.propTypes = {
  scanner: scannerPropType,
  scannerId: PropTypes.string,
  organizationId: PropTypes.string,
  isFetching: PropTypes.bool,
  scannerRuns: PropTypes.arrayOf(scannerRunPropType),
  isFetchingScannerRuns: PropTypes.bool,
  scannerRunsError: PropTypes.shape({
    message: PropTypes.string
  }),
  loadScanners: PropTypes.func,
  loadScannerRuns: PropTypes.func,
  clearScannerRuns: PropTypes.func,
  onRunNowClick: PropTypes.func,
  onAbortClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  setAlert: PropTypes.func
};

ScannerDetail.defaultProps = {
  scannerRuns: [],
  isFetchingScannerRuns: false,
  scannerRunsError: null,
  loadScanners: () => {},
  loadScannerRuns: () => {},
  clearScannerRuns: () => {},
  onRunNowClick: () => {},
  onAbortClick: () => {},
  onDeleteClick: () => {},
  onEditClick: () => {},
  setAlert: () => {}
};

export default ScannerDetail;
